import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { ReturnToStoreFormValues, ReturnToStoreFormSchema } from "./types";
import useTranslation from "@babackapp/translations/use-translation";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import PremiumFeaturBadge from "@babackapp/ui/components/premium-feature-badge";
import { cn } from "@/utils/cn";

type ReturnToStoreFormProps = {
  onSubmit: (values: ReturnToStoreFormValues) => void;
  defaultValues?: ReturnToStoreFormValues;
};

export default function ReturnToStoreForm({
  onSubmit,
  defaultValues,
}: ReturnToStoreFormProps) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();

  const form = useForm<ReturnToStoreFormValues>({
    resolver: zodResolver(ReturnToStoreFormSchema),
    defaultValues: {
      returnToStoreActivated: true,
      ...defaultValues,
    },
  });

  const activated = useWatch({
    control: form.control,
    name: "returnToStoreActivated",
  });

  useEffect(() => {
    if (form.formState.isDirty) {
      const values = form.getValues();
      onSubmit(values);
      form.reset(values);
    }
  }, [activated, form, onSubmit]);

  const disabled = shop.plan.returnToStore
    ? false
    : t("settings.entrepriseFeatureDescription");

  /**
   * I'm using a form just in case it expends, and to
   * fit the design of the other forms in the app.
   * But it's not really necessary here.
   */
  return (
    <Form {...form}>
      <form className="flex flex-col gap-8 px-4">
        <div
          className={cn(
            "rounded-md p-2 flex justify-between",
            disabled && "bg-[#ffa73209] p-4",
          )}
        >
          <FormField
            control={form.control}
            name="returnToStoreActivated"
            render={({ field }) => (
              <FormSwitch
                disabled={disabled}
                label={t("settings.returnToStore.fields.activation.title")}
                description={t(
                  "settings.returnToStore.fields.activation.description",
                )}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {disabled && <PremiumFeaturBadge />}
        </div>
      </form>
    </Form>
  );
}
